import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription, catchError, map, of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { OrigemCadastroProdutoEnum } from '@shared/enums/produto/origem-cadastro-produto.enum';
import { ProdutoListaRefinamentoEnum } from '@shared/enums/produto/produto-lista-refinamento.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { montarUrlCadastroProduto } from '@shared/helpers/produto-cadastro-origem.helper';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CompraService } from '@shared/services/apis/compras/compra.service';
import { ProdutoService } from '@shared/services/apis/produto/produto.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { AssociarProdutoItemAvulsoRequest } from '@shared/services/requests/compras/associar-produto-item-avulso.request';
import { AssociarProdutoItensAvulsosRequest } from '@shared/services/requests/compras/associar-produto-itens-avulsos.request';
import { CompraItemAvulsoStruct } from '@shared/services/structs/compras/compra-item-avulso.struct';

/** Abertura do modal: a compra e os itens avulsos devolvidos na recusa do avanço para Pedido. */
export class AssociarProdutosAvulsosDialogData {
  idCompra: number;
  itensAvulsos: CompraItemAvulsoStruct[] = [];
}

/** Fechamento com sucesso (vínculo já gravado). Cancelar fecha com `undefined`. */
export class AssociarProdutosAvulsosDialogResult {
  confirmado = false;
  vinculados: {
    idCompraItem: number;
    idProduto: number;
    codigoSku: string | null;
    nome: string;
    marca: string | null;
    unidade: string | null;
  }[] = [];
  idsRemovidos: number[] = [];
}

/** Produto do cadastro escolhido na busca para um item avulso. */
class ProdutoEscolhido {
  idProduto: number;
  codigoSku: string | null = null;
  nome: string;
  marca: string | null = null;
  unidade: string | null = null;
}

/** Estado de tela de um item avulso. Pendente = sem produto e sem remoção. */
class LinhaItemAvulso {
  item: CompraItemAvulsoStruct;
  produto: ProdutoEscolhido | null = null;
  isRemover = false;
  // Busca no cadastro
  isBuscaAberta = false;
  termoBusca = '';
  resultados: ProdutoEscolhido[] = [];
  isPesquisando = false;
}

const TOOLTIP_SEM_PERMISSAO = 'Você não tem permissão para cadastrar produtos. Vincule a um produto existente.';
const TOOLTIP_CADASTRO_COMPLETO = 'Abre o cadastro de produto em nova aba, com descrição, unidade e observação do item preenchidas. Depois de salvar, volte e busque o produto aqui.';

/**
 * "Vincular itens avulsos": antes de avançar para Pedido de Compra, cada item avulso é vinculado a
 * um produto do cadastro (busca; o cadastro completo abre em nova aba) ou removido da compra. Detecta produto
 * repetido (já presente na compra ou em outro avulso) e só libera a confirmação sem pendências.
 * Ao confirmar grava o vínculo em lote e fecha com o resultado; o chamador refaz a movimentação.
 */
@Component({
  selector: 'app-associar-produtos-avulsos-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './associar-produtos-avulsos-dialog.component.html',
  styleUrl: './associar-produtos-avulsos-dialog.component.css',
})
export class AssociarProdutosAvulsosDialogComponent implements OnInit, OnDestroy {
  linhas: LinhaItemAvulso[] = [];
  podeCadastrar = false;
  isCarregando = true;
  isConfirmando = false;
  readonly tooltipSemPermissao = TOOLTIP_SEM_PERMISSAO;
  readonly tooltipCadastroCompleto = TOOLTIP_CADASTRO_COMPLETO;

  /** Itens não avulsos já na compra (idProduto → nome), para detectar produto repetido. */
  private produtosNaCompra = new Map<number, string>();
  private readonly busca$ = new Subject<{ linha: LinhaItemAvulso; termo: string }>();
  private readonly subs = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<AssociarProdutosAvulsosDialogComponent, AssociarProdutosAvulsosDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: AssociarProdutosAvulsosDialogData,
    private compraService: CompraService,
    private produtoService: ProdutoService,
    private authService: AuthService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    const usuario = this.authService.getUserInfos();
    this.podeCadastrar = !!usuario?.master
      || !!usuario?.listaPermissoes?.includes(PermissaoEnum.Cadastros_Produtos_Inserir);

    this.linhas = (this.data.itensAvulsos || []).map(item => {
      const linha = new LinhaItemAvulso();
      linha.item = item;
      return linha;
    });

    this.assinarBusca();
    this.carregarItensDaCompra();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ---------------------------------------------------------------- carga

  private carregarItensDaCompra(): void {
    this.subs.add(
      this.compraService.getDetalhe(this.data.idCompra).subscribe({
        next: (res) => {
          this.isCarregando = false;
          if (res.isError) {
            this.alertService.show('Aviso', res.errorDescription || 'Não foi possível carregar os itens da compra.', AlertType.warning);
            return;
          }
          const idsAvulsos = new Set(this.linhas.map(l => l.item.idCompraItem));
          for (const i of res.itens || []) {
            if (!i.isAvulso && i.idProduto && !idsAvulsos.has(i.idCompraItem)) {
              this.produtosNaCompra.set(i.idProduto, i.nome);
            }
          }
        },
        error: () => {
          this.isCarregando = false;
          this.alertService.show('Erro', 'Não foi possível carregar os itens da compra.', AlertType.error);
        },
      }),
    );
  }

  // ---------------------------------------------------------------- estado e divergência

  isPendente(linha: LinhaItemAvulso): boolean {
    return !linha.isRemover && !linha.produto;
  }

  /** Texto da divergência do item, ou null quando não há. Não soma quantidades. */
  divergencia(linha: LinhaItemAvulso): string | null {
    if (linha.isRemover || !linha.produto) return null;
    const idProduto = linha.produto.idProduto;
    const nomeNaCompra = this.produtosNaCompra.get(idProduto);
    const outroAvulso = this.linhas.find(l => l !== linha && !l.isRemover && l.produto?.idProduto === idProduto);
    const nome = nomeNaCompra ?? outroAvulso?.item.nome;
    if (!nome) return null;
    return `Este produto já está na compra como "${nome}". Troque o produto ou remova este item avulso.`;
  }

  get totalPendentes(): number {
    return this.linhas.filter(l => this.isPendente(l)).length;
  }

  /** Resolvido = vinculado sem divergência ou marcado para remoção (progresso do rodapé). */
  get totalResolvidos(): number {
    return this.linhas.filter(l => !this.isPendente(l) && this.divergencia(l) === null).length;
  }

  get totalDivergentes(): number {
    return this.linhas.filter(l => this.divergencia(l) !== null).length;
  }

  /** Motivo do "Confirmar e mover" desabilitado (tooltip). A carga dos itens só desabilita, sem aviso de espera. */
  get motivoConfirmarDesabilitado(): string {
    if (this.totalPendentes) {
      return this.totalPendentes === 1
        ? 'Vincule ou remova o item avulso pendente.'
        : `Vincule ou remova os ${this.totalPendentes} itens avulsos pendentes.`;
    }
    if (this.totalDivergentes) return 'Resolva os itens com produto repetido: troque o produto ou remova o item avulso.';
    return '';
  }

  formatarQuantidade(quantidade: number): string {
    return (quantidade ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
  }

  metaProduto(p: ProdutoEscolhido): string {
    return [p.codigoSku && `SKU ${p.codigoSku}`, p.marca, p.unidade].filter(Boolean).join(' · ');
  }

  // ---------------------------------------------------------------- busca no cadastro

  private assinarBusca(): void {
    this.subs.add(
      this.busca$.pipe(
        debounceTime(300),
        switchMap(({ linha, termo }) => {
          // Assinatura única para todos os campos: a busca de outro item cancela a anterior.
          for (const l of this.linhas) l.isPesquisando = false;
          linha.isPesquisando = true;
          linha.resultados = [];
          return this.produtoService.getAll({
            searchText: termo,
            idProdutoListaRefinamento: ProdutoListaRefinamentoEnum.Descricao,
            isAtivo: true,
          }, 0).pipe(
            map(res => {
              if (res.isError) {
                this.alertService.show('Erro', res.errorDescription || 'Não foi possível buscar os produtos.', AlertType.error);
              }
              return { linha, lista: res.isError ? [] : (res.list || []) };
            }),
            catchError(() => {
              this.alertService.show('Erro', 'Não foi possível buscar os produtos.', AlertType.error);
              return of({ linha, lista: [] });
            }),
          );
        }),
      ).subscribe(({ linha, lista }) => {
        linha.isPesquisando = false;
        linha.resultados = lista.slice(0, 8).map(p => {
          const produto = new ProdutoEscolhido();
          produto.idProduto = p.idProduto;
          produto.codigoSku = p.codigoSku || null;
          produto.nome = p.descricao;
          produto.marca = p.marca || null;
          produto.unidade = p.unidade || null;
          return produto;
        });
      }),
    );
  }

  /** Reabre a busca num item já vinculado (Trocar); o item pendente já mostra o campo. */
  abrirBusca(linha: LinhaItemAvulso): void {
    linha.isBuscaAberta = true;
    linha.termoBusca = '';
    linha.resultados = [];
  }

  fecharBusca(linha: LinhaItemAvulso): void {
    linha.isBuscaAberta = false;
    linha.isPesquisando = false;
    linha.resultados = [];
  }

  /** Mantém o campo vazio ao escolher uma opção (o valor da opção é o produto, não texto). */
  readonly exibirVazio = (): string => '';

  alterarTermoBusca(linha: LinhaItemAvulso, valor: unknown): void {
    // Ao escolher uma opção o autocomplete emite o próprio produto; só texto dispara busca.
    if (typeof valor !== 'string') return;
    linha.termoBusca = valor;
    const termo = valor.trim();
    if (termo.length < 2) {
      linha.resultados = [];
      return;
    }
    this.busca$.next({ linha, termo });
  }

  escolherProduto(linha: LinhaItemAvulso, produto: ProdutoEscolhido): void {
    linha.produto = produto;
    this.fecharBusca(linha);
  }

  // ---------------------------------------------------------------- cadastro completo

  /** Cadastro completo em nova aba; depois de salvar lá, o usuário volta e usa a busca. */
  abrirCadastroCompleto(linha: LinhaItemAvulso): void {
    if (!this.podeCadastrar) return;
    // O cadastro busca o item pelo id e pré-preenche descrição, unidade e observação.
    window.open(montarUrlCadastroProduto(OrigemCadastroProdutoEnum.CompraItem, linha.item.idCompraItem), '_blank', 'noopener');
  }

  // ---------------------------------------------------------------- troca e remoção

  trocarProduto(linha: LinhaItemAvulso): void {
    this.abrirBusca(linha);
  }

  removerItem(linha: LinhaItemAvulso): void {
    linha.isRemover = true;
    linha.isBuscaAberta = false;
  }

  desfazerRemocao(linha: LinhaItemAvulso): void {
    linha.isRemover = false;
  }

  // ---------------------------------------------------------------- rodapé

  cancelar(): void {
    this.dialogRef.close(undefined);
  }

  confirmar(): void {
    if (this.isConfirmando || this.isCarregando || this.motivoConfirmarDesabilitado) return;

    const result = new AssociarProdutosAvulsosDialogResult();
    result.confirmado = true;
    result.vinculados = this.linhas
      .filter(l => !l.isRemover && l.produto)
      .map(l => ({
        idCompraItem: l.item.idCompraItem,
        idProduto: l.produto!.idProduto,
        codigoSku: l.produto!.codigoSku,
        nome: l.produto!.nome,
        marca: l.produto!.marca,
        unidade: l.produto!.unidade,
      }));
    result.idsRemovidos = this.linhas.filter(l => l.isRemover).map(l => l.item.idCompraItem);

    const request = new AssociarProdutoItensAvulsosRequest();
    request.itens = result.vinculados.map(v => {
      const item = new AssociarProdutoItemAvulsoRequest();
      item.idCompraItem = v.idCompraItem;
      item.idProduto = v.idProduto;
      item.codigoSku = v.codigoSku;
      item.nome = v.nome;
      item.marca = v.marca;
      item.unidade = v.unidade;
      return item;
    });
    request.idsCompraItemRemover = result.idsRemovidos;

    this.isConfirmando = true;
    this.subs.add(
      this.compraService.associarProdutoItensAvulsos(this.data.idCompra, request).subscribe({
        next: (res) => {
          this.isConfirmando = false;
          if (res.isError) {
            this.alertService.show('Aviso', res.errorDescription || 'Não foi possível vincular os itens avulsos.', AlertType.warning);
            return;
          }
          this.dialogRef.close(result);
        },
        error: () => {
          this.isConfirmando = false;
          this.alertService.show('Erro', 'Não foi possível vincular os itens avulsos.', AlertType.error);
        },
      }),
    );
  }
}
